import type React from 'react';
import { Button, Modal } from 'shared-portal-components-v2';

import FileUploadProgress from '@/components/molecules/FileUploadProgress/FileUploadProgress';
import type { UploadProgress } from '@/types/poc/helia';

interface FileUploadProgressModalProps {
  isOpen: boolean;
  currentUpload: UploadProgress | null;
  currentUploadFileName: string;
  currentUploadError: string | null;
  encryptionSteps: Array<{
    id: string;
    label: string;
    status: 'pending' | 'loading' | 'success' | 'error';
  }>;
  uploadedFileCount: number;
  totalFileCount: number;
  canRetryCurrentUpload: boolean;
  isUploading: boolean;
  onClose: () => void;
  onDone: () => void;
  onRetryCurrentUpload: () => void;
}

function getStatusHeading(currentUpload: UploadProgress | null, currentUploadError: string | null): string {
  if (currentUploadError != null || currentUpload?.stage === 'error') {
    return 'Upload failed';
  }

  if (currentUpload?.stage === 'complete') {
    return 'Upload Complete!';
  }

  return 'Uploading...';
}

function getStatusCopy(totalFileCount: number, uploadedFileCount: number, isFinished: boolean): string {
  if (isFinished) {
    return `${String(totalFileCount)} file(s) uploaded successfully`;
  }

  return `${String(uploadedFileCount)} of ${String(totalFileCount)} file(s) uploaded successfully`;
}

interface UploadProgressModalActionsProps {
  canRetryCurrentUpload: boolean;
  hasError: boolean;
  isFinished: boolean;
  isUploading: boolean;
  onClose: () => void;
  onDone: () => void;
  onRetryCurrentUpload: () => void;
}

function UploadProgressModalActions({
  canRetryCurrentUpload,
  hasError,
  isFinished,
  isUploading,
  onClose,
  onDone,
  onRetryCurrentUpload,
}: UploadProgressModalActionsProps): React.JSX.Element {
  if (isFinished) {
    return (
      <Button className="w-full" variant="primary" size="md" onClick={onDone}>
        Done
      </Button>
    );
  }

  if (isUploading) {
    return (
      <Button className="w-full" variant="primary" size="md" disabled>
        Uploading...
      </Button>
    );
  }

  if (hasError && canRetryCurrentUpload) {
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <Button variant="outline" size="md" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={onRetryCurrentUpload}>
          Retry upload
        </Button>
      </div>
    );
  }

  return (
    <div className="flex justify-end">
      <Button variant="outline" size="md" onClick={onClose}>
        Close
      </Button>
    </div>
  );
}

function UploadProgressSection({
  canRetryCurrentUpload,
  currentUpload,
  currentUploadError,
  currentUploadFileName,
  hasError,
  onRetryCurrentUpload,
}: {
  canRetryCurrentUpload: boolean;
  currentUpload: UploadProgress | null;
  currentUploadError: string | null;
  currentUploadFileName: string;
  hasError: boolean;
  onRetryCurrentUpload: () => void;
}): React.JSX.Element {
  return (
    <FileUploadProgress
      uploadId={currentUpload?.uploadId ?? 'active-upload'}
      fileName={currentUploadFileName}
      progress={currentUpload?.progress ?? 0}
      stage={currentUpload?.stage ?? (hasError ? 'error' : 'encrypting')}
      error={currentUploadError ?? currentUpload?.error}
      retryCount={currentUpload?.retryCount ?? 0}
      variant="embedded"
      onRetry={canRetryCurrentUpload ? () => {
        onRetryCurrentUpload();
      } : undefined}
    />
  );
}

export default function FileUploadProgressModal({
  isOpen,
  currentUpload,
  currentUploadFileName,
  currentUploadError,
  encryptionSteps,
  uploadedFileCount,
  totalFileCount,
  canRetryCurrentUpload,
  isUploading,
  onClose,
  onDone,
  onRetryCurrentUpload,
}: FileUploadProgressModalProps): React.JSX.Element {
  const isFinished = !isUploading && currentUploadError == null && uploadedFileCount >= totalFileCount;
  const hasError = currentUploadError != null || currentUpload?.stage === 'error';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Upload Files"
      ariaLabel="Upload Files"
      panelClassName="!w-full !max-w-2xl max-h-[min(90vh,48rem)] flex flex-col"
    >
      <div className="space-y-5 overflow-y-auto pr-1">
        <div className="text-center">
          <h2 className="text-[2rem] font-semibold tracking-[-0.02em] text-foreground">
            {getStatusHeading(currentUpload, currentUploadError)}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {getStatusCopy(totalFileCount, uploadedFileCount, isFinished)}
          </p>
        </div>

        <div className="rounded-xl border border-border bg-background px-6 py-5">
          <div className="space-y-6">
            {!isFinished && (
              <UploadProgressSection
                canRetryCurrentUpload={canRetryCurrentUpload}
                currentUpload={currentUpload}
                currentUploadError={currentUploadError}
                currentUploadFileName={currentUploadFileName}
                hasError={hasError}
                onRetryCurrentUpload={onRetryCurrentUpload}
              />
            )}

            <UploadProgressModalActions
              canRetryCurrentUpload={canRetryCurrentUpload}
              hasError={hasError}
              isFinished={isFinished}
              isUploading={isUploading}
              onClose={onClose}
              onDone={onDone}
              onRetryCurrentUpload={onRetryCurrentUpload}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
